import { createRef, useCallback, useEffect, useState } from 'react';
import CustomForm from 'components/form/CustomForm';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import uuid from 'react-uuid';
import Form from '@rjsf/core';
import { SectionTitle } from 'components/form/SectionTitle';
import { AddFieldButton, RemoveFieldButton, SchemaPropertyCard } from './Form.styles';
import { LLMClassifyConfiguration, LLMClassifySchemaProperty, LLMClassifySchema, CreateWorkflowNode } from 'api/model';
import {
  NodeFormProps,
  schemaLLMClassifySchemaProperty,
  uiSchemaLLMClassifySchemaProperty,
} from 'api/workflows-steps/formSchema';
import { toast } from 'react-toastify';
import { useNodeValidation } from 'hooks/useNodeValidation';
import LLMCommonForm from './LLMCommonForm';

type SchemaClassifyProperty = {
  id: string;
  ref?: React.MutableRefObject<Form>;
  data?: LLMClassifySchemaProperty;
};

const ClassifySchemaForm = (props: NodeFormProps) => {
  const { initialValues = {} as CreateWorkflowNode } = props;
  const [formData, setFormData] = useState<CreateWorkflowNode>(initialValues);
  const [classifySchemaProperty, setClassifySchemaProperty] = useState<SchemaClassifyProperty[]>([]);

  const { validate } = useNodeValidation(initialValues.type);

  useEffect(() => {
    const init = initialValues;
    if (init.configuration) {
      const conf = init.configuration as LLMClassifyConfiguration;
      const buff: SchemaClassifyProperty[] = [];
      for (const p in conf.classify_schema.properties) {
        const data = conf.classify_schema.properties[p];
        buff.push({
          id: uuid(),
          ref: createRef() as React.MutableRefObject<Form>,
          data: {
            ...data,
            field_key: p,
            classes: data.classes,
          } as LLMClassifySchemaProperty,
        });
      }
      setClassifySchemaProperty(buff);
    }
  }, [initialValues]);

  const handleSubmit = useCallback(async () => {
    const isValid: boolean = classifySchemaProperty.filter((p) => !p.ref?.current?.validateForm()).length == 0;

    const conf = formData.configuration as LLMClassifyConfiguration | undefined;
    if (
      !conf?.classify_schema ||
      !conf.classify_schema.properties ||
      Object.keys(conf.classify_schema.properties).length === 0
    ) {
      toast.error("Classify schema can't be empty! Add some fields.");
      return;
    }
    if (!isValid || !(await validate(formData))) {
      return;
    }
    props.onSubmit?.(formData);
  }, [formData, props.onSubmit, classifySchemaProperty]);

  const handleBaseFormOnChange = (formDataToChange: CreateWorkflowNode) => {
    const newFormData = {
      ...formDataToChange,
      configuration: {
        ...formDataToChange.configuration,
        classify_schema: (formData.configuration as LLMClassifyConfiguration | undefined)?.classify_schema,
      },
    };

    setFormData(newFormData);
    props.onChange?.(newFormData);
  };

  const handlePropertyOnChange = (tempFormData: LLMClassifySchemaProperty, id: string) => {
    const property = classifySchemaProperty.find((p) => p.id == id);
    if (property) {
      const index = classifySchemaProperty.indexOf(property);
      if (index != -1) {
        classifySchemaProperty[index] = {
          ...property,
          data: tempFormData,
        } as SchemaClassifyProperty;
      }
    }
    setClassifySchemaProperty(classifySchemaProperty);

    updateFormData(classifySchemaProperty);
  };

  const updateFormData = (classifyProperties: SchemaClassifyProperty[]) => {
    const classifySchema: LLMClassifySchema = {
      properties: classifyProperties.reduce(
        (a, p) => ({
          ...a,
          [p.data?.field_key ?? '']: {
            type: p.data?.type,
            description: p.data?.description,
            classes: p.data?.classes,
          },
        }),
        {},
      ),
      required:
        // classifyProperties.filter((p) => p.data?.required && p.data?.field_key).map((p) => p.data!.field_key) ?? [],
        [],
    };

    setFormData({ ...formData, configuration: { ...formData.configuration, classify_schema: classifySchema } });
  };

  const handleAddClassifySchemaProperty = () => {
    setClassifySchemaProperty([
      ...classifySchemaProperty,
      {
        id: uuid(),
        ref: createRef(),
      } as SchemaClassifyProperty,
    ]);
  };

  const handleDeleteClassifySchemaProperty = (id: string) => {
    const properties = classifySchemaProperty.filter((p) => p.id != id);
    setClassifySchemaProperty(properties);
    updateFormData(properties);
  };

  return (
    <>
      <LLMCommonForm {...props} onSubmit={handleSubmit} onChange={handleBaseFormOnChange} validate={false} />
      <SectionTitle title="Classify schema" />

      <div className="flex flex-col">
        {classifySchemaProperty.map((p) => (
          <SchemaPropertyCard key={p.id}>
            <CustomForm<LLMClassifySchemaProperty>
              schema={schemaLLMClassifySchemaProperty}
              uiSchema={uiSchemaLLMClassifySchemaProperty}
              onSubmit={handleSubmit}
              onChange={(data) => handlePropertyOnChange(data, p.id)}
              initialValues={p.data}
              formId={`${props.formId}-${p.id}`}
              ref={p.ref}
              className="w-full"
              disabled={props.disabled}
            />
            <div className="flex items-center pl-1">
              <RemoveFieldButton
                aria-label="delete"
                size="small"
                onClick={() => handleDeleteClassifySchemaProperty(p.id)}
                disabled={props.disabled}
              >
                <DeleteIcon fontSize="small" />
              </RemoveFieldButton>
            </div>
          </SchemaPropertyCard>
        ))}
        <AddFieldButton
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddClassifySchemaProperty}
          disabled={props.disabled}
        >
          Add classify field
        </AddFieldButton>
      </div>
    </>
  );
};

export default ClassifySchemaForm;
